import { useState } from 'react';
import type { PendingContinue } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Pencil, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  pendingContinue: PendingContinue;
  disabled?: boolean;
  onContinue: (args: { instruction?: string; turbo?: boolean }) => void;
  onDone: () => void;
}

/** Shown after the agent hit a successful `render` with step-approval mode
 *  on. User picks: continue (same goal, next pass), modify (steer with
 *  an instruction), turbo (run to completion without further stops), or
 *  done (accept the current spec, end the session of prompts). */
export function ContinuePrompt({
  pendingContinue,
  disabled,
  onContinue,
  onDone,
}: Props) {
  const { t } = useTranslation('common');
  const [mode, setMode] = useState<'idle' | 'modify'>('idle');
  const [draft, setDraft] = useState('');

  if (mode === 'modify') {
    return (
      <div className="rounded-lg border-2 border-brand-indigo/30 bg-brand-cream p-3 space-y-2">
        <div className="text-xs font-bold text-brand-indigo">
          {t('chat.modifyQuestion')}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const text = draft.trim();
            if (!text) return;
            onContinue({ instruction: text });
            setDraft('');
            setMode('idle');
          }}
          className="flex gap-1.5"
        >
          <input
            type="text"
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={disabled}
            placeholder={t('chat.modifyPlaceholder')}
            className="flex-1 rounded-md border-[1.5px] border-k-line bg-k-card px-2.5 py-1.5 text-xs text-k-ink placeholder:text-k-ink-faint focus:border-brand-indigo focus:outline-none"
          />
          <Button
            type="submit"
            size="sm"
            disabled={disabled || !draft.trim()}
            className="h-8 px-2.5"
          >
            {t('chat.send')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={disabled}
            onClick={() => {
              setDraft('');
              setMode('idle');
            }}
            className="h-8 px-2.5 text-synergy-neutral-400"
          >
            {t('chat.back')}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 border-brand-teal bg-brand-teal-soft/30 p-3 space-y-2">
      <div className="text-xs font-semibold text-brand-indigo">
        {pendingContinue.summary}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onContinue({})}
          disabled={disabled}
          className="h-8 px-2.5 text-xs"
        >
          <ArrowRight className="h-3 w-3 mr-1" />
          {t('chat.continue')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setMode('modify')}
          disabled={disabled}
          className="h-7 px-2.5 text-xs"
        >
          <Pencil className="h-3 w-3 mr-1" />
          {t('chat.modify')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onContinue({ turbo: true })}
          disabled={disabled}
          className="h-7 px-2.5 text-xs"
          title={t('chat.turboTitle')}
        >
          <Zap className="h-3 w-3 mr-1" />
          {t('chat.turboMode')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDone}
          disabled={disabled}
          className="h-7 px-2.5 text-xs text-synergy-neutral-400"
        >
          <Check className="h-3 w-3 mr-1" />
          {t('chat.done')}
        </Button>
      </div>
    </div>
  );
}
